import React, { useCallback, useEffect, useState, useContext, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Prompt } from 'react-router-dom';
import { ListAlt } from '@mui/icons-material';
import CollapseOrBlankCard from 'components/common/CollapseOrBlankCard';
import RtiInsuranceGeneralData from './RtiInsuranceGeneralData';
import { set, useChangeTrackingLens } from '@totalsoft/change-tracking-react';
import { Switch, FormControlLabel, Grid, Typography, Tooltip } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useQueryWithErrorHandling } from 'customHooks/errorHandling';
import { INSURANCE_SIMULATION_FOR_RTI } from '../../../queries';
import { useApolloClient, useMutation, useReactiveVar, useSubscription } from '@apollo/client';
import { UPDATE_RTI_OPTION, UPDATE_RTI_PERIODICITY } from '../../../mutations';
import { RTI_OPTION_UPDATED, INSURANCE_FINANCING_OFFER_SAVED, INSURANCE_STATE_CODE_UPDATED_FAILED } from '../../../subscriptions';
import { RTI_OPTION_UPDATED_EVENT, INSURANCE_FINANCING_OFFER_SAVED_EVENT } from '../../../eventTypes';
import { useToast, useActivityIndicatorContext } from 'utils/hooks';
import { emptyObject } from 'constants/emptyConst';
import { useGlobalSubscription } from 'providers/GlobalCacheManager';
import { isDirty } from '@totalsoft/change-tracking';
import { RightsContext } from 'providers/RightsContext';
import { cascoPolicyDocumentNumberVar, cascoQuotationsProcessingVar, cascoSelectionInProgressVar } from '../../../variables';
import { useInsuranceTypeConfiguration } from 'utils/hooks/useInsuranceTypeConfiguration';
import useInsuranceParams from '../../../useInsuranceParams';

const RtiInsuranceContainer = ({ documentDetail, simulation }) => {
  const { t } = useTranslation();
  const client = useApolloClient();
  const { onBeginCall, onEndCall } = useActivityIndicatorContext();
  const addToast = useToast();

  const { readOnlyFull, isImage, offerInsuranceWrite } = useContext(RightsContext);
  const insuranceTypeConfig = useInsuranceTypeConfiguration();
  const { offerId, offerSiteId } = useInsuranceParams();
  const cascoQuotationsProcessing = useReactiveVar(cascoQuotationsProcessingVar);
  const cascoSelectionInProgress = useReactiveVar(cascoSelectionInProgressVar);
  const policyDocumentNumber = useReactiveVar(cascoPolicyDocumentNumberVar);

  const [saving, setSaving] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [lens, dirtyInfo, resetLens] = useChangeTrackingLens();

  // #region Memos

  const isReadOnly = useMemo(
    () =>
      !simulation?.rtiEligible ||
      cascoQuotationsProcessing ||
      cascoSelectionInProgress ||
      !offerInsuranceWrite ||
      readOnlyFull ||
      isImage ||
      saving ||
      policyDocumentNumber,
    [
      simulation?.rtiEligible,
      cascoQuotationsProcessing,
      cascoSelectionInProgress,
      offerInsuranceWrite,
      readOnlyFull,
      isImage,
      saving,
      policyDocumentNumber
    ]
  );
  const insuranceSimulationId = useMemo(() => simulation.id, [simulation]);
  const isLensDirty = useMemo(() => isDirty(dirtyInfo), [dirtyInfo]);

  // #endregion

  const { loading, data, refetch } = useQueryWithErrorHandling(INSURANCE_SIMULATION_FOR_RTI, {
    variables: { id: insuranceSimulationId, offerId, offerSiteId },
    cacheEviction: true,
    skip: !insuranceSimulationId,
    onCompleted: data => {
      const _simulation = data?.insuranceSimulation;
      if (_simulation?.hasRtiOption) {
        cascoPolicyDocumentNumberVar(_simulation?.policyDocumentNumber);
      }
    }
  });

  const { rtiOption: rtiInsurance, hasRtiOption } = useMemo(() => data?.insuranceSimulation || emptyObject, [data]);

  // #region Subscription

  useGlobalSubscription(RTI_OPTION_UPDATED, {
    variables: { insuranceSimulationId },
    onSubscriptionData: () => {
      addToast(t('Notifications.' + RTI_OPTION_UPDATED_EVENT), 'info');
      setSaving(false);
      onEndCall();
    }
  });

  useSubscription(INSURANCE_FINANCING_OFFER_SAVED, {
    variables: { insuranceSimulationId, insuranceTypeCode: insuranceTypeConfig.rti?.propertyCode },
    onSubscriptionData: ({
      subscriptionData: {
        data: { insuranceFinancingOfferSaved }
      }
    }) => {
      if (!insuranceFinancingOfferSaved) return;

      if (!insuranceFinancingOfferSaved?.quotationsInvalidated) {
        setSaving(false);
        onEndCall();
        addToast(
          t('Notifications.' + INSURANCE_FINANCING_OFFER_SAVED_EVENT, {
            data: { insuranceTypeName: insuranceTypeConfig.rti?.insuranceTypeName }
          }),
          'info'
        );
      }
      resetLens(prev => ({ ...prev }));
      refetch({ id: insuranceSimulationId, offerId, offerSiteId });
    }
  });

  useGlobalSubscription(INSURANCE_STATE_CODE_UPDATED_FAILED, {
    variables: { insuranceSimulationId },
    onSubscriptionData: ({
      subscriptionData: {
        data: { externalInsuranceStateCodeUpdateFailed }
      }
    }) => {
      setSaving(false);
      onEndCall();
      if (externalInsuranceStateCodeUpdateFailed.message) addToast(externalInsuranceStateCodeUpdateFailed.message, 'error');
    }
  });

  // #endregion

  // #region Update RTI Periodicity

  const [updateRtiPeriodicity] = useMutation(UPDATE_RTI_PERIODICITY, {
    context: {
      headers: { offerId, offerSiteId }
    },
    onError: err => {
      setSaving(false);
      onEndCall();
      addToast(err.message, 'error');
    },
    onCompleted: _ => {
      const simulationCacheId = client.cache.identify({
        __typename: 'RtiOption',
        insuranceSimulationId
      });
      client.cache.evict({ id: simulationCacheId, fieldName: 'periodicity' });
      client.cache.gc();
    }
  });

  const handleUpdateRtiPeriodicity = useCallback(
    periodicity => {
      (lens.periodicity |> set)(periodicity);
      onBeginCall();
      setSaving(true);
      updateRtiPeriodicity({
        variables: {
          insuranceSimulationId,
          paymentPeriodicity: periodicity
        }
      });
    },
    [onBeginCall, insuranceSimulationId, lens, updateRtiPeriodicity]
  );

  // #endregion

  // #region Update RTI option

  const [updateRtiOption] = useMutation(UPDATE_RTI_OPTION, {
    context: {
      headers: { offerId, offerSiteId }
    },
    onError: err => {
      addToast(err.message, 'error');
      onEndCall();
      setSaving(false);
    }
  });

  const handleUpdateRtiOption = useCallback(
    event => {
      const value = event.target.checked;
      onBeginCall();
      setSaving(true);
      updateRtiOption({
        variables: {
          insuranceSimulationId,
          rtiOption: value,
          offerId,
          offerSiteId
        }
      });
    },
    [updateRtiOption, insuranceSimulationId, offerId, offerSiteId, onBeginCall]
  );

  // #endregion

  const onExpand = useCallback((_, expanded) => setExpanded(!expanded), []);

  useEffect(() => {
    if (loading || !rtiInsurance || isLensDirty) return;

    resetLens({
      currencyCode: documentDetail.currencyCode,
      insuranceAmount: rtiInsurance?.insuranceAmount,
      financedInsurance: Boolean(rtiInsurance?.financedInsurance),
      financedInsurancePeriod: rtiInsurance?.financedPeriod,
      periodicity: rtiInsurance?.periodicity,
      insurerName: rtiInsurance?.insurerName,
      duration: data?.leasingSimulationByOffer?.duration,
      quantity: documentDetail.quantity,
      insuranceAmountPerGood: rtiInsurance?.insuranceAmount / documentDetail.quantity,
      externalInsuranceDocumentId: rtiInsurance?.externalInsuranceDocumentId,
      externalInsuranceDocumentNumber: rtiInsurance?.externalInsuranceDocumentNumber
    });
  }, [loading, data, rtiInsurance, documentDetail, resetLens, isLensDirty]);

  return (
    <>
      <Prompt when={isLensDirty} message={() => t('SaveChanges', { card: t('Offer.Insurance.RTI') })} />
      <CollapseOrBlankCard
        id={'rtiInsuranceCard'}
        icon={ListAlt}
        iconColor={'success'}
        expanded={expanded}
        onToggle={onExpand}
        canExpand={!!rtiInsurance}
        title={
          <Grid container spacing={2} alignItems='center' justifyContent='flex-start'>
            <Grid item>
              <Typography variant='h6'>{t('Offer.Insurance.RTI')}</Typography>
            </Grid>
            <Grid item>
              <Tooltip title={t('Offer.Insurance.RtiInsuranceOption')}>
                <FormControlLabel
                  control={
                    <Switch
                      id={'rtiInsurance_check'}
                      color={'primary'}
                      checked={hasRtiOption ?? false}
                      onChange={handleUpdateRtiOption}
                      disabled={isReadOnly}
                    />
                  }
                  onClick={event => event.stopPropagation()}
                  onFocus={event => event.stopPropagation()}
                />
              </Tooltip>
            </Grid>
          </Grid>
        }
      >
        <RtiInsuranceGeneralData lens={lens} onUpdatePaymentPeriodicity={handleUpdateRtiPeriodicity} disabled={isReadOnly} />
      </CollapseOrBlankCard>
    </>
  );
};

RtiInsuranceContainer.propTypes = {
  documentDetail: PropTypes.object.isRequired,
  simulation: PropTypes.object.isRequired
};

export default RtiInsuranceContainer;
